import React, { useCallback, useEffect, useState } from "react";
import { DashboardEditorProps } from "components/ComponentType";
import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import {
  defaultDatasetLineChartConfig,
  LineEditorLoadingTypes,
  LoadConfigUnitTypes,
} from "components/dashboard/data-input-output-table/defaultConfigs";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { T } from "services/lang/LanguageService";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import {
  IProductTemplateDropdownChangeEvent,
  ProductTemplateDropdown,
} from "components/basic/dropdowns/product-template/ProductTemplateDropdown";
import ProductTemplateColumnsDropdown from "components/basic/dropdowns/product-template-columns-dropdown/ProductTemplateColumnsDropdown";
import { MultiSelectChangeEvent } from "primereact/multiselect";
import LineEditor from "./line-editor/LineEditor";
import {
  DatasetChartProps,
  LagLeadLoadingConfig,
  LoadingConfig,
  ProductEntryLoadingConfig,
} from "../interfaces";

export const DefaultDatasetLoadingConf: LagLeadLoadingConfig = {
  type: LineEditorLoadingTypes.TIME,
  lead: 0,
  lag: 6,
  lagUnit: "HOURS",
  leadUnit: "HOURS",
  alignUnit: null,
};

const DefaultDatasetLoadingConfProductEntry: ProductEntryLoadingConfig = {
  type: LineEditorLoadingTypes.PRODUCT_ENTRY,
  productTemplateId: "",
  startAttribute: "",
  endAttribute: "",
  refName: "",
};

function DatasetLineChartEditor({
  config,
  onConfigChange,
}: DashboardEditorProps<DatasetChartProps>) {
  const conf = config && Object.keys(config).length !== 0 ? config : defaultDatasetLineChartConfig;

  const [currentConfig, setCurrentConfig] = useState<DatasetChartProps>(conf);
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([]);
  const [lineEditorDialogVisible, setLineEditorDialogVisibility] = useState<boolean>(false);
  const [productTemplateCreationDate, setProductTemplateCreationDate] = useState<
    Date | undefined
  >();

  useEffect(() => {
    const initialSelectedDatasets =
      currentConfig.datasets?.map(dataset => dataset.identifier) ?? [];
    setSelectedDatasets(initialSelectedDatasets);
  }, [currentConfig.datasets]);

  const handleConfigChange = useCallback(
    (e: InputNumberValueChangeEvent | InputSwitchChangeEvent): void => {
      setCurrentConfig(prev => ({ ...prev, [e.target.id]: e.target.value }));
    },
    []
  );

  const handleDatasetChange = useCallback(
    (selectedIdentifiers: string[]): void => {
      const selectedDatasetsArray = selectedIdentifiers.map(identifier => {
        const oldDataset = currentConfig.datasets?.find(
          dataset => dataset.identifier === identifier
        );
        return oldDataset ?? { identifier };
      });

      setCurrentConfig(prev => ({ ...prev, datasets: selectedDatasetsArray }));
      setSelectedDatasets(selectedIdentifiers);
    },
    [currentConfig.datasets]
  );

  const handleLoadConfChange = useCallback((e: DropdownChangeEvent): void => {
    const selectedType = e.value;
    const updatedLoadConf =
      selectedType === LineEditorLoadingTypes.PRODUCT_ENTRY
        ? DefaultDatasetLoadingConfProductEntry
        : DefaultDatasetLoadingConf;
    setCurrentConfig(prev => ({
      ...prev,
      loadConf: { ...updatedLoadConf, type: selectedType },
    }));
  }, []);

  const handleLoadConfDetailsChange = useCallback(
    (e: InputNumberValueChangeEvent | DropdownChangeEvent) => {
      if (currentConfig.loadConf?.type === LineEditorLoadingTypes.TIME) {
        const key = e.target.id as keyof LagLeadLoadingConfig;
        const value = Number.isNaN(e.target.value)
          ? e.target.value.toString().toUpperCase()
          : e.target.value;
        if (
          key === LoadConfigUnitTypes.ALIGNUNIT ||
          key === LoadConfigUnitTypes.LEADUNIT ||
          key === LoadConfigUnitTypes.LAGUNIT
        ) {
          setCurrentConfig(prev => ({
            ...prev,
            loadConf: { ...prev.loadConf, [key]: value.toString().toUpperCase() } as LoadingConfig,
          }));
        } else {
          setCurrentConfig(prev => ({
            ...prev,
            loadConf: { ...prev.loadConf, [key]: value } as LoadingConfig,
          }));
        }
      }
    },
    [currentConfig.loadConf]
  );

  const handleProductEntryIdChange = useCallback((e: IProductTemplateDropdownChangeEvent) => {
    setCurrentConfig(prev => ({
      ...prev,
      loadConf: {
        ...prev.loadConf,
        productTemplateId: e.value ? e.value.toString() : "",
        startAttribute: "",
        endAttribute: "",
        refName: e.refName ? e.refName.toString() : "",
      } as LoadingConfig,
    }));
  }, []);

  const handleProductEntryColumnsChange = useCallback((e: MultiSelectChangeEvent) => {
    const value = e.value.length > 0 ? e.value[0].toString() : "";
    setCurrentConfig(prev => ({
      ...prev,
      loadConf: {
        ...prev.loadConf,
        [e.target.id]: value,
      } as LoadingConfig,
    }));
  }, []);

  const handleEditLinesClick = useCallback(() => {
    setLineEditorDialogVisibility(true);
  }, []);

  useEffect(() => {
    if (currentConfig.datasets) {
      onConfigChange(currentConfig);
    }
  }, [currentConfig]);

  useEffect(() => {
    if (productTemplateCreationDate) {
      setCurrentConfig(prev => ({ ...prev, productTemplateCreationDate }));
    }
  }, [productTemplateCreationDate]);

  return (
    <div className="grid p-fluid flex flex-column">
      <div className="col-12 flex flex-column mb-3">
        <label htmlFor="identifiers">{T("word.identifiers")}</label>
        <div className="flex gap-2">
          <DatasetMultiSelect value={selectedDatasets} onChange={handleDatasetChange} />
          {selectedDatasets.length > 0 && (
            <>
              <Button
                icon="pi pi-pencil"
                type="button"
                className="p-button-secondary"
                tooltip={T("action.edit-lines")}
                onClick={handleEditLinesClick}
              />
              <Dialog
                onHide={() => setLineEditorDialogVisibility(false)}
                visible={lineEditorDialogVisible}
                header={T("action.edit-line-style")}
                style={{ width: "min(100vw, 1000px)", height: "400px" }}
              >
                <LineEditor currentConfig={currentConfig} setCurrentConfig={setCurrentConfig} />
              </Dialog>
            </>
          )}
        </div>
      </div>
      <div className="flex flex-wrap justify-content-between p-2 gap-2">
        <div className="w-8rem flex flex-column justify-content-between mb-3">
          <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="updateFrequency"
              value={currentConfig.updateFrequency as number}
              onValueChange={handleConfigChange}
            />
            <span className="p-inputgroup-addon">s</span>
          </div>
        </div>
        <div className="w-8rem flex flex-column justify-content-between mb-3">
          <label htmlFor="timeAxisStepSeconds">{T("chart-config.time-axis-step")}</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="timeAxisStepSeconds"
              value={currentConfig.timeAxisStepSeconds as number}
              onValueChange={handleConfigChange}
            />
            <span className="p-inputgroup-addon">s</span>
          </div>
        </div>
        <div className="w-8rem flex flex-column justify-content-between mb-3">
          <label htmlFor="height">{T("word.height")}</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="height"
              value={currentConfig.height as number}
              onValueChange={handleConfigChange}
            />
            <span className="p-inputgroup-addon">px</span>
          </div>
        </div>
        <div className="w-8rem flex flex-column justify-content-between mb-3">
          <label htmlFor="hideRefresh">{T("chart-config.hide-refresh")}</label>
          <InputSwitch
            id="hideRefresh"
            checked={Boolean(currentConfig.hideRefresh)}
            onChange={handleConfigChange}
          />
        </div>
        <div className="w-8rem flex flex-column justify-content-between mb-3">
          <label htmlFor="toggleTracesGrouping">{T("action.group-traces")}</label>
          <InputSwitch
            id="toggleTracesGrouping"
            checked={Boolean(currentConfig.toggleTracesGrouping)}
            onChange={handleConfigChange}
          />
        </div>
        <div className="w-8rem flex flex-column justify-content-between mb-3">
          <label htmlFor="showDateSelection">{T("chart-config.show-date-selection")}</label>
          <InputSwitch
            id="showDateSelection"
            checked={Boolean(currentConfig.showDateSelection)}
            onChange={handleConfigChange}
          />
        </div>
      </div>
      <div className="gap-2 flex flex-column mb-3 ml-2 w-full md:w-4">
        <label htmlFor="loadConf">{T("chart-config.loading-type")}</label>
        <Dropdown
          id="loadConf"
          value={currentConfig.loadConf?.type}
          options={Object.values(LineEditorLoadingTypes).map(type => ({
            label: type,
            value: type,
          }))}
          onChange={handleLoadConfChange}
        />
      </div>

      {currentConfig.loadConf && currentConfig.loadConf.type === LineEditorLoadingTypes.TIME && (
        <div className="flex flex-column">
          <div className="flex justify-content-between gap-3 ml-2">
            <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="lag">{T("load-conf.lag")}</label>
              <div className="p-inputgroup">
                <InputNumber
                  id="lag"
                  value={currentConfig.loadConf.lag}
                  onValueChange={handleLoadConfDetailsChange}
                />
                <TimeUnitDropdown
                  id="lagUnit"
                  className="p-inputgroup-addon p-0"
                  value={currentConfig.loadConf.lagUnit}
                  onChange={handleLoadConfDetailsChange}
                />
              </div>
            </div>
            <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="lead">{T("load-conf.lead")}</label>
              <div className="p-inputgroup">
                <InputNumber
                  id="lead"
                  value={currentConfig.loadConf.lead}
                  onValueChange={handleLoadConfDetailsChange}
                />
                <TimeUnitDropdown
                  id="leadUnit"
                  className="p-inputgroup-addon p-0"
                  value={currentConfig.loadConf.leadUnit}
                  onChange={handleLoadConfDetailsChange}
                />
              </div>
            </div>
            <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="alignUnit">{T("load-conf.align-unit")}</label>
              <TimeUnitDropdown
                id="alignUnit"
                className="w-full"
                value={currentConfig.loadConf.alignUnit || ""}
                onChange={handleLoadConfDetailsChange}
              />
            </div>
          </div>
        </div>
      )}
      {currentConfig.loadConf?.type === LineEditorLoadingTypes.PRODUCT_ENTRY && (
        <div className="flex flex-column md:flex-row justify-content-between ml-2 gap-3">
          <div className="flex flex-column gap-2 w-full md:w-6">
            <label htmlFor="productTemplateId">{T("load-conf.product-template")}</label>
            <ProductTemplateDropdown
              value={parseInt(currentConfig.loadConf.productTemplateId, 10)}
              onChange={handleProductEntryIdChange}
            />
          </div>
          <div className="flex flex-column gap-3 w-full md:w-6">
            <div className="flex flex-column gap-2">
              <label htmlFor="startAttribute">{T("load-conf.start-attribute")}</label>
              <ProductTemplateColumnsDropdown
                id="startAttribute"
                value={
                  currentConfig.loadConf.startAttribute
                    ? [currentConfig.loadConf.startAttribute]
                    : []
                }
                onChange={e => handleProductEntryColumnsChange(e)}
                selectedTemplate={{
                  refName: currentConfig.loadConf.refName ?? "",
                  value: parseInt(currentConfig.loadConf.productTemplateId, 10),
                }}
                showClear={false}
                setProductTemplateCreationDate={setProductTemplateCreationDate}
              />
            </div>
            <div className="flex flex-column gap-2">
              <label htmlFor="endAttribute">{T("load-conf.end-attribute")}</label>
              <ProductTemplateColumnsDropdown
                id="endAttribute"
                value={
                  currentConfig.loadConf.endAttribute ? [currentConfig.loadConf.endAttribute] : []
                }
                onChange={e => handleProductEntryColumnsChange(e)}
                selectedTemplate={{
                  refName: currentConfig.loadConf.refName ?? "",
                  value: parseInt(currentConfig.loadConf.productTemplateId, 10),
                }}
                showClear={false}
                setProductTemplateCreationDate={setProductTemplateCreationDate}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatasetLineChartEditor;
